import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Server,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Activity,
  Database,
  Brain,
  FileText,
  Workflow,
  FlaskConical,
  ClipboardList,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

const STATUS_COLORS = {
  healthy: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle },
  degraded: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: AlertTriangle },
  down: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: XCircle },
  unknown: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', text: 'text-slate-400', icon: AlertTriangle },
};

const COMPONENT_ICONS = {
  'Parser Engine': Brain,
  'AI Analysis': Activity,
  'Knowledge Base': Database,
  'Letter Generator': FileText,
  'Workflow Engine': Workflow,
  'Sandbox Runner': FlaskConical,
  'Review Queue': ClipboardList,
};

const ComponentCard = ({ component }) => {
  const statusConfig = STATUS_COLORS[component.status] || STATUS_COLORS.unknown;
  const StatusIcon = statusConfig.icon;
  const ComponentIcon = COMPONENT_ICONS[component.name] || Server;

  return (
    <div 
      className={`${statusConfig.bg} border ${statusConfig.border} rounded-xl p-5`}
      data-testid={`status-${component.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${statusConfig.bg} flex items-center justify-center`}>
            <ComponentIcon className={statusConfig.text} size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{component.name}</h3>
            <p className={`text-sm capitalize ${statusConfig.text}`}>{component.status}</p>
          </div>
        </div>
        <StatusIcon className={statusConfig.text} size={20} />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-stone-800/50 rounded-lg p-2">
          <p className="text-xs text-slate-500">Response Time</p>
          <p className="text-sm text-white font-medium">
            {component.response_time_ms ? `${component.response_time_ms}ms` : 'N/A'}
          </p>
        </div>
        <div className="bg-stone-800/50 rounded-lg p-2">
          <p className="text-xs text-slate-500">Errors (24h)</p>
          <p className="text-sm text-white font-medium">{component.error_count_24h || 0}</p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-700/50">
        <p className="text-xs text-slate-500">
          Last check: {new Date(component.last_check).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
};

const SystemStatusPage = () => {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.getSystemStatus();
      setStatus(data);
    } catch (error) {
      toast.error('Failed to load system status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const overallStatusConfig = STATUS_COLORS[status?.overall_status] || STATUS_COLORS.unknown;
  const OverallIcon = overallStatusConfig.icon;

  return (
    <div className="space-y-6" data-testid="system-status-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Server className="text-amber-400" size={28} />
            System Status
          </h1>
          <p className="text-slate-500 mt-1">
            Monitor the health and performance of all system components
          </p>
        </div>
        <Button variant="outline" onClick={fetchStatus} disabled={loading}>
          <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall Status Banner */}
      {loading ? (
        <Skeleton className="h-24 bg-stone-800" />
      ) : (
        <div className={`${overallStatusConfig.bg} border ${overallStatusConfig.border} rounded-xl p-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl ${overallStatusConfig.bg} flex items-center justify-center`}>
                <OverallIcon className={overallStatusConfig.text} size={28} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white capitalize">
                  {status?.overall_status || 'Unknown'}
                </h2>
                <p className="text-slate-400">
                  All {status?.components?.length || 0} components monitored
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Last updated</p>
              <p className="text-white">
                {status?.last_check ? new Date(status.last_check).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Component Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(status?.components || []).map((component, index) => (
            <ComponentCard key={index} component={component} />
          ))}
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 text-center">
        <p className="text-sm text-slate-500">
          Status checks run automatically every 30 seconds. All components are monitored for availability and performance.
        </p>
      </div>
    </div>
  );
};

export default SystemStatusPage;
